import { config as appConfig } from '@/core'
import { FormOfAddress, FREE_LANGUAGE_TAGS } from './types'

export function debounce<T extends unknown[], U>(callback: (...args: T) => PromiseLike<U> | U, wait: number) {
  let timer: ReturnType<typeof setTimeout>

  return (...args: T): Promise<U> => {
    clearTimeout(timer)
    return new Promise((resolve) => {
      timer = setTimeout(() => resolve(callback(...args)), wait)
    })
  }
}

export const getAmplitudeApiKey = () => {
  switch (appConfig.app.env) {
    case 'local':
      return process.env.AMPLITUDE_LOCAL_API_KEY
    case 'test':
      return process.env.AMPLITUDE_STAGE_API_KEY
    case 'production':
      return process.env.AMPLITUDE_PROD_API_KEY
    default:
      console.error('Unrecognized Amplitude Environment')
  }
}

export function getLanguageLabel(tag: string | undefined, language: string): string {
  if (!tag) return '–'

  try {
    return (
      new Intl.DisplayNames(language, {
        type: 'language',
        languageDisplay: 'dialect',
        fallback: 'code',
      }).of(tag) ?? tag
    )
  } catch {
    return tag
  }
}

export const isFormalityApplicable = (form_of_address: string): boolean => {
  return form_of_address.toLowerCase() !== FormOfAddress.NOT_APPLICABLE
}

export const isFreeLanguageTag = (tag: string): boolean => (FREE_LANGUAGE_TAGS as readonly string[]).includes(tag)

type LDContext = {
  kind: string
  key: string
  name?: string
  custom?: Record<string, unknown>
}

type LDMultiContext = {
  kind: 'multi'
  app: LDContext
  tenant?: LDContext
}

/**
 * Generic function to check LaunchDarkly feature flags via direct API call
 * @param context - LaunchDarkly context (can be single or multi-context)
 * @param flagName - The name of the feature flag in kebab-case format
 * @returns Promise<boolean> - Returns true if flag is enabled, false otherwise
 */
export const checkLDFeatureFlag = async (context: LDContext | LDMultiContext, flagName: string): Promise<boolean> => {
  try {
    const response = await fetch(
      `https://clientsdk.launchdarkly.com/sdk/evalx/${appConfig.launchDarkly.clientSideId}/contexts/${btoa(
        JSON.stringify(context),
      )}`,
      {
        method: 'GET',
        headers: {
          Authorization: appConfig.launchDarkly.clientSideId,
          'Content-Type': 'application/json',
        },
      },
    )

    if (response.ok) {
      const data = await response.json()
      return Boolean(data[flagName]?.value)
    }

    return false
  } catch (error) {
    console.error(`LaunchDarkly flag check failed for ${flagName}:`, error)
    return false
  }
}

/**
 * Helper function to check LaunchDarkly feature flags for a specific tenant
 * Uses multi-context with app and tenant contexts
 * @param subdomain - The tenant subdomain
 * @param flagName - The name of the feature flag in kebab-case format
 * @returns Promise<boolean> - Returns true if flag is enabled, false otherwise
 */
export const checkTenantFeatureFlag = async (subdomain: string, flagName: string): Promise<boolean> => {
  const context: LDMultiContext = {
    kind: 'multi',
    app: {
      kind: 'app',
      key: 'laas-ui',
    },
    tenant: {
      kind: 'tenant',
      key: subdomain,
    },
  }
  return checkLDFeatureFlag(context, flagName)
}
